/**
 * ImportCarDealerModal — wizard de 4 pasos para importar modelos de auto
 * desde Excel hacia el módulo Car Dealer.
 *
 * Pasos:
 *   0 → Inicio (descripción + descargar plantilla)
 *   1 → Subir archivo (drag&drop, .xlsx)
 *   2 → Revisar (preview + errores + confirmar)
 *   3 → Resultado (creados / actualizados / errores)
 *
 * Backend: /api/car-dealer/import/{template,parse,execute}
 */
import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Upload, FileSpreadsheet, FileDown, Loader2, CheckCircle2, XCircle,
  AlertTriangle, ArrowLeft, ArrowRight, Sparkles, RefreshCcw, ChevronDown, ChevronUp,
  ClipboardList, X,
} from 'lucide-react';

const fmtPrice = (value) => {
  if (value === null || value === undefined || value === '' || isNaN(Number(value))) return '-';
  try {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency', currency: 'MXN', maximumFractionDigits: 0,
    }).format(Number(value));
  } catch {
    return `$${Number(value).toLocaleString('es-MX')}`;
  }
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const ImportCarDealerModal = ({ open, onClose, onSuccess }) => {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [parseRes, setParseRes] = useState(null);
  const [executeRes, setExecuteRes] = useState(null);
  const [parseError, setParseError] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [showResultErrors, setShowResultErrors] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsItems, setLogsItems] = useState([]);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!showLogs) return;
    let cancelled = false;
    (async () => {
      setLogsLoading(true);
      try {
        const r = await api.get('/car-dealer/import/logs');
        if (!cancelled) setLogsItems(r.data?.items || []);
      } catch (e) {
        if (!cancelled) {
          toast.error(e.response?.data?.detail || 'Error al cargar historial');
          setLogsItems([]);
        }
      } finally {
        if (!cancelled) setLogsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showLogs]);

  const reset = () => {
    setStep(0);
    setFile(null);
    setParseRes(null);
    setExecuteRes(null);
    setParseError('');
    setShowErrors(false);
    setShowResultErrors(false);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  // ── Step 0: descargar plantilla ──
  const handleDownloadTemplate = async () => {
    setTemplateLoading(true);
    try {
      const r = await api.get('/car-dealer/import/template', { responseType: 'blob' });
      downloadBlob(r.data, 'plantilla_modelos_auto.xlsx');
      toast.success('Plantilla descargada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al descargar plantilla');
    } finally {
      setTemplateLoading(false);
    }
  };

  // ── Step 1: subir archivo + parse ──
  const handleFileSelected = async (selected) => {
    if (!selected) return;
    if (!selected.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Solo se aceptan archivos .xlsx');
      return;
    }
    setFile(selected);
    setParseError('');
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', selected);
      const r = await api.post('/car-dealer/import/parse', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setParseRes(r.data);
      setStep(2);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Error al analizar el archivo';
      setParseError(msg);
      setFile(null);
    } finally {
      setParsing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFileSelected(f);
  };

  // ── Step 2: ejecutar import ──
  const handleExecute = async () => {
    if (!parseRes) return;
    setExecuting(true);
    try {
      const r = await api.post('/car-dealer/import/execute', {
        session_id: parseRes.session_id,
        confirmed: true,
      });
      setExecuteRes(r.data);
      setStep(3);
    } catch (e) {
      setExecuteRes({ success: false, error: e.response?.data?.detail || 'Error al ejecutar la importación' });
      setStep(3);
    } finally {
      setExecuting(false);
    }
  };

  const handleFinish = () => {
    onSuccess?.();
    handleClose();
  };

  // ── Stepper ──
  const stepLabels = ['Inicio', 'Subir', 'Revisar', 'Resultado'];
  const renderStepper = () => (
    <div className="flex items-center gap-1 mb-3" data-testid="car-import-stepper">
      {stepLabels.map((label, idx) => (
        <div key={label} className="flex items-center gap-1 flex-1">
          <div
            className={`h-1 flex-1 rounded-full ${idx <= step ? 'bg-emerald-500' : 'bg-white/10'}`}
            data-testid={`car-import-stepper-bar-${idx}`}
          />
          <span className={`text-[9px] uppercase shrink-0 ${idx === step ? 'text-emerald-300 font-bold' : 'text-muted-foreground'}`}>
            {label}
          </span>
        </div>
      ))}
    </div>
  );

  // ── Render por paso ──
  const renderStep0 = () => (
    <div className="space-y-4" data-testid="car-import-step-0">
      <div className="rounded-xl border border-white/10 bg-black/20 p-4 space-y-2">
        <p className="text-sm font-semibold text-white">Importar modelos de auto</p>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Sube tu Excel con el inventario. Los campos no incluidos quedarán
          vacíos para que los completes después desde el formulario de edición.
        </p>
      </div>

      <Card
        onClick={handleDownloadTemplate}
        className="cursor-pointer hover:border-blue-400/40 bg-black/20 border-white/10 transition"
        data-testid="car-import-download-template"
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2">
            {templateLoading ? <Loader2 className="w-5 h-5 text-blue-400 animate-spin" /> : <FileSpreadsheet className="w-5 h-5 text-blue-400" />}
            <span className="text-sm font-semibold text-white">Descargar plantilla</span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Genera un .xlsx con todas las columnas, hints e instrucciones, y un
            ejemplo real de Chevrolet Trax RS 2026.
          </p>
        </CardContent>
      </Card>

      <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
        <p className="text-[11px] text-amber-300">
          También puedes usar un Excel propio si tiene las columnas
          obligatorias: <strong>Marca</strong>, <strong>Modelo</strong> y{' '}
          <strong>Versión / Gama / Trim</strong>.
        </p>
      </div>

      <Card
        onClick={() => setShowLogs(true)}
        className="cursor-pointer hover:border-purple-400/40 bg-black/20 border-white/10 transition"
        data-testid="car-import-open-logs"
      >
        <CardContent className="p-4 space-y-1">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-purple-400" />
            <span className="text-sm font-semibold text-white">
              Historial de importaciones
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Consulta quién importó modelos y cuándo.
          </p>
        </CardContent>
      </Card>

      <div className="flex justify-end pt-2">
        <Button
          onClick={() => setStep(1)}
          className="bg-emerald-600 hover:bg-emerald-500 text-white"
          data-testid="car-import-continue-step-0"
        >
          Continuar <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-4" data-testid="car-import-step-1">
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
        className="rounded-xl border-2 border-dashed border-white/20 bg-black/20 p-8 text-center cursor-pointer hover:border-primary/40 transition"
        data-testid="car-import-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          onChange={(e) => handleFileSelected(e.target.files?.[0])}
          className="hidden"
          data-testid="car-import-file-input"
        />
        {parsing ? (
          <div className="space-y-2">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-emerald-400" />
            <p className="text-sm text-white">Analizando archivo...</p>
            <p className="text-[11px] text-muted-foreground">{file?.name}</p>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload className="w-8 h-8 mx-auto text-muted-foreground" />
            <p className="text-sm text-white">Arrastra tu archivo aquí o haz clic para seleccionar</p>
            <p className="text-[11px] text-muted-foreground">Solo formato .xlsx (máx 10 MB)</p>
          </div>
        )}
      </div>

      {parseError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 flex items-start gap-2" data-testid="car-import-parse-error">
          <XCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
          <p className="text-xs text-red-300">{parseError}</p>
        </div>
      )}

      <div className="flex justify-between pt-1">
        <Button variant="ghost" onClick={() => setStep(0)} data-testid="car-import-step-1-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver
        </Button>
      </div>
    </div>
  );

  const renderStep2 = () => {
    const valid = parseRes?.valid_rows || 0;
    const errCount = parseRes?.error_rows || 0;
    const skipped = parseRes?.skipped_rows || 0;
    const unknown = parseRes?.unknown_columns || [];
    const errorsDetail = parseRes?.errors_detail || [];
    const preview = parseRes?.preview || [];

    return (
      <div className="space-y-3" data-testid="car-import-step-2">
        <div className="grid grid-cols-3 gap-2">
          <Card className="bg-black/20 border-white/10">
            <CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase">Modelos listos</p>
              <p className="text-xl font-bold text-emerald-300" data-testid="car-import-valid-count">{valid}</p>
            </CardContent>
          </Card>
          <Card className={`border-white/10 ${errCount > 0 ? 'bg-red-500/10' : 'bg-black/20'}`}>
            <CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase">Errores</p>
              <p className={`text-xl font-bold ${errCount > 0 ? 'text-red-300' : 'text-white'}`} data-testid="car-import-error-count">{errCount}</p>
            </CardContent>
          </Card>
          <Card className="bg-black/20 border-white/10">
            <CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase">Saltados</p>
              <p className="text-xl font-bold text-zinc-300" data-testid="car-import-skipped-count">{skipped}</p>
            </CardContent>
          </Card>
        </div>

        {unknown.length > 0 && (
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 space-y-2" data-testid="car-import-unknown-columns">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <p className="text-xs font-semibold text-amber-300">Columnas no reconocidas (se ignoraron)</p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {unknown.map((c) => (
                <Badge key={c} className="bg-amber-500/10 text-amber-300 border border-amber-500/30 text-[10px]">
                  {c}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {preview.length > 0 && (
          <div className="rounded-xl border border-white/10 overflow-hidden">
            <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
              <table className="w-full text-xs" data-testid="car-import-preview-table">
                <thead className="bg-black/40 sticky top-0">
                  <tr className="text-left text-muted-foreground">
                    <th className="p-2">Marca</th>
                    <th className="p-2">Modelo</th>
                    <th className="p-2">Trim</th>
                    <th className="p-2">Carrocería</th>
                    <th className="p-2 text-right">Precio lista</th>
                    <th className="p-2 text-right">Precio financiado</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((r) => (
                    <tr key={r.row} className="border-t border-white/5 hover:bg-white/5" data-testid={`car-import-preview-row-${r.row}`}>
                      <td className="p-2 text-white">{r.brand || '-'}</td>
                      <td className="p-2 text-white">{r.model || '-'}</td>
                      <td className="p-2 text-zinc-300">{r.trim || '-'}</td>
                      <td className="p-2 text-zinc-300">{r.body_type || '-'}</td>
                      <td className="p-2 text-right tabular-nums text-zinc-100">{fmtPrice(r.list_price)}</td>
                      <td className="p-2 text-right tabular-nums text-zinc-100">{fmtPrice(r.dealer_special_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {valid > preview.length && (
              <p className="p-2 text-[10px] text-muted-foreground text-center bg-black/30">
                Mostrando preview de {preview.length} de {valid} filas. La importación procesa todas.
              </p>
            )}
          </div>
        )}

        {errorsDetail.length > 0 && (
          <div className="rounded-lg border border-red-500/20 bg-red-500/5" data-testid="car-import-errors-section">
            <button
              type="button"
              onClick={() => setShowErrors((s) => !s)}
              className="w-full flex items-center justify-between p-3 hover:bg-red-500/10 transition"
              data-testid="car-import-errors-toggle"
            >
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-400" />
                <p className="text-xs font-semibold text-red-300">Errores por fila ({errorsDetail.length})</p>
              </div>
              {showErrors ? <ChevronUp className="w-3.5 h-3.5 text-red-300" /> : <ChevronDown className="w-3.5 h-3.5 text-red-300" />}
            </button>
            {showErrors && (
              <div className="border-t border-red-500/20 max-h-[200px] overflow-y-auto p-3 space-y-1">
                {errorsDetail.map((er, i) => (
                  <div key={`${er.row}-${i}`} className="flex items-start gap-2 text-[11px]">
                    <Badge className="bg-red-500/20 text-red-300 border border-red-500/30 text-[10px] shrink-0">
                      Fila {er.row}
                    </Badge>
                    <span className="text-red-300 leading-relaxed">{er.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-between pt-2">
          <Button variant="ghost" onClick={() => { setStep(1); setParseRes(null); }} disabled={executing} data-testid="car-import-step-2-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <Button
            onClick={handleExecute}
            disabled={executing || valid === 0}
            className="bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
            data-testid="car-import-execute-btn"
          >
            {executing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Confirmar e importar
          </Button>
        </div>
      </div>
    );
  };

  const renderStep3 = () => {
    if (!executeRes) return null;
    if (executeRes.success === false || executeRes.error) {
      return (
        <div className="space-y-4 text-center" data-testid="car-import-step-3-error">
          <XCircle className="w-12 h-12 mx-auto text-red-400" />
          <p className="text-base font-semibold text-white">Error en la importación</p>
          <p className="text-sm text-muted-foreground">{executeRes.error || 'Ocurrió un error desconocido.'}</p>
          <div className="flex justify-center gap-2">
            <Button onClick={reset} variant="outline" className="border-white/20" data-testid="car-import-retry-btn">
              <RefreshCcw className="w-4 h-4 mr-1" /> Reintentar
            </Button>
            <Button onClick={handleClose} variant="ghost">Cerrar</Button>
          </div>
        </div>
      );
    }

    const imported = executeRes.imported || 0;
    const updated = executeRes.updated || 0;
    const skipped = executeRes.skipped || 0;
    const errors = executeRes.errors || [];
    const totalOk = imported + updated;

    return (
      <div className="space-y-4" data-testid="car-import-step-3-success">
        <div className="text-center space-y-2">
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400" />
          <p className="text-lg font-semibold text-white">¡Listo!</p>
          <p className="text-sm text-muted-foreground">
            Se procesaron <strong className="text-emerald-300">{totalOk}</strong> modelo{totalOk === 1 ? '' : 's'} correctamente.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Card className="bg-black/20 border-white/10">
            <CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase">Creados</p>
              <p className="text-xl font-bold text-emerald-300" data-testid="car-import-result-created">{imported}</p>
            </CardContent>
          </Card>
          <Card className="bg-black/20 border-white/10">
            <CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase">Actualizados</p>
              <p className="text-xl font-bold text-blue-300" data-testid="car-import-result-updated">{updated}</p>
            </CardContent>
          </Card>
          <Card className={`border-white/10 ${errors.length > 0 ? 'bg-red-500/10' : 'bg-black/20'}`}>
            <CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase">Saltados / Errores</p>
              <p className={`text-xl font-bold ${errors.length > 0 ? 'text-red-300' : 'text-zinc-300'}`} data-testid="car-import-result-errors">
                {skipped + errors.length}
              </p>
            </CardContent>
          </Card>
        </div>

        {errors.length > 0 && (
          <div className="rounded-lg border border-red-500/20 bg-red-500/5" data-testid="car-import-result-errors-section">
            <button
              type="button"
              onClick={() => setShowResultErrors((s) => !s)}
              className="w-full flex items-center justify-between p-3 hover:bg-red-500/10 transition"
              data-testid="car-import-result-errors-toggle"
            >
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-400" />
                <p className="text-xs font-semibold text-red-300">Detalle de errores ({errors.length})</p>
              </div>
              {showResultErrors ? <ChevronUp className="w-3.5 h-3.5 text-red-300" /> : <ChevronDown className="w-3.5 h-3.5 text-red-300" />}
            </button>
            {showResultErrors && (
              <div className="border-t border-red-500/20 max-h-[200px] overflow-y-auto p-3 space-y-1">
                {errors.map((er, i) => (
                  <div key={`${er.row}-${i}`} className="flex items-start gap-2 text-[11px]">
                    <Badge className="bg-red-500/20 text-red-300 border border-red-500/30 text-[10px] shrink-0">
                      Fila {er.row}
                    </Badge>
                    <span className="text-red-300 leading-relaxed">{er.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-between pt-2">
          <Button onClick={reset} variant="outline" className="border-white/20" data-testid="car-import-another-btn">
            <FileDown className="w-4 h-4 mr-1" /> Importar otro archivo
          </Button>
          <Button onClick={handleFinish} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="car-import-finish-btn">
            Cerrar
          </Button>
        </div>
      </div>
    );
  };

  const renderLogsPanel = () => {
    const fmtDate = (iso) => {
      if (!iso) return '-';
      try {
        return new Intl.DateTimeFormat('es-MX', {
          dateStyle: 'medium', timeStyle: 'short',
        }).format(new Date(iso));
      } catch { return iso; }
    };

    return (
      <div className="space-y-3" data-testid="car-import-logs-panel">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-purple-400" />
            <h3 className="text-sm font-semibold text-white">
              Historial de importaciones
            </h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowLogs(false)}
            className="text-zinc-400 hover:text-white"
            data-testid="car-import-logs-close"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {logsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
          </div>
        ) : logsItems.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-black/20 p-8 text-center">
            <p className="text-sm text-muted-foreground">
              Aún no hay importaciones registradas
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-white/10 overflow-hidden">
            <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
              <table className="w-full text-xs" data-testid="car-import-logs-table">
                <thead className="bg-black/40 sticky top-0">
                  <tr className="text-left text-muted-foreground">
                    <th className="p-2">Fecha</th>
                    <th className="p-2">Usuario</th>
                    <th className="p-2">Archivo</th>
                    <th className="p-2 text-right">Importadas</th>
                    <th className="p-2 text-right">Actualizadas</th>
                    <th className="p-2 text-right">Saltadas</th>
                    <th className="p-2 text-right">Fallidas</th>
                  </tr>
                </thead>
                <tbody>
                  {logsItems.map((log) => (
                    <tr
                      key={log.id}
                      className="border-t border-white/5 hover:bg-white/5"
                      data-testid={`car-import-log-row-${log.id}`}
                    >
                      <td className="p-2 text-zinc-300 whitespace-nowrap">
                        {fmtDate(log.completed_at || log.uploaded_at)}
                      </td>
                      <td className="p-2 text-zinc-400">{log.user_email || '-'}</td>
                      <td
                        className="p-2 text-white truncate max-w-[160px]"
                        title={log.filename}
                      >
                        {log.filename || '-'}
                      </td>
                      <td className="p-2 text-right text-emerald-300 font-semibold">
                        {log.imported || 0}
                      </td>
                      <td className="p-2 text-right text-blue-300 font-semibold">
                        {log.updated || 0}
                      </td>
                      <td className="p-2 text-right text-zinc-400 font-semibold">
                        {log.skipped || 0}
                      </td>
                      <td className={`p-2 text-right font-semibold ${
                        (log.errors_count || 0) > 0 ? 'text-red-300' : 'text-zinc-400'
                      }`}>
                        {log.errors_count || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card/95 backdrop-blur border-white/10" data-testid="car-import-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
            Importar modelos de auto
          </DialogTitle>
        </DialogHeader>

        {showLogs ? renderLogsPanel() : (
          <>
            {renderStepper()}

            {step === 0 && renderStep0()}
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImportCarDealerModal;
